import { useState, useContext } from "react";
import { useForm, Controller } from "react-hook-form";
// Context
import { AlertNotificationContext } from "../contexts/AlertNotificationContext";
// Hooks
import { useEditAddress } from "../hooks/useEditAddress";
import { useCheckAPI } from "../hooks/useCheckAPI";
import { useProtectedContent } from "../hooks/useProtectedContent";
import { useDebounce } from "../hooks/useDebounce";
// State
import { useAddressAutoComplete } from "../state/addressAutoCompleteState";
import { useAddress } from "../state/addressState";
import { useUserBranches } from "../state/userBranchListState";
// Components
import { RHFSelectField } from "./RHFSelectField";
// MUI
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormHelperText from "@mui/material/FormHelperText";
import Skeleton from "@mui/material/Skeleton";

export const EditAddressDialog = ({ address: addressProp }) => {
  // --- Mutations ---
  const {
    mutate: mutateEdit,
    isSuccess: editSuccess,
    data: editData,
    isPending: editPending,
  } = useEditAddress();
  const {
    mutate: mutateDelete,
    isSuccess: deleteSuccess,
    data: deleteData,
    isPending: deletePending,
  } = useEditAddress();

  // --- Local state ---
  const [open, setOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const { setAlertNotification } = useContext(AlertNotificationContext);

  // --- Address lookup ---
  const [searchAddress, setSearchAddress] = useState("");
  const [searchAddressID, setSearchAddressID] = useState("");
  const debouncedValue = useDebounce(searchAddress, 1000);
  const [addressOptions, setAddressOptions] = useState([]);

  // Build default values from the address prop
  const buildDefaults = () => ({
    name: addressProp.name || "",
    address: addressProp.address || "",
    address2: addressProp.address2 || "",
    county: addressProp.county || "",
    city: addressProp.city || "",
    postcode: addressProp.postCode || "",
    country: addressProp.country || "GB",
    residential: addressProp.homeaddress === "Yes",
    contact: addressProp.contact || "",
    contactEmail: addressProp.email || "",
    contactMobile: addressProp.mobileNo || "",
    branchNo: addressProp.branchNo || "",
  });

  // --- Form ---
  const {
    handleSubmit,
    reset,
    control: controlAddress,
    setValue: setValueAddress,
    watch,
  } = useForm({ defaultValues: buildDefaults() });

  const watchResidential = watch("residential", false);

  // --- Address autocomplete ---
  const { data: autoCompleteData, isSuccess: autocompleteSuccess } =
    useAddressAutoComplete(
      { params: { address: debouncedValue } },
      debouncedValue ? true : false,
    );

  const autocompleteSuccessFunc = () => {
    setAddressOptions(autoCompleteData.data);
  };
  const autocompleteErrorFunc = () =>
    setAlertNotification({
      message: `Error: ${autoCompleteData.status}: ${autoCompleteData.statusText}`,
      severity: "error",
    });

  useCheckAPI(
    autocompleteSuccess,
    autoCompleteData,
    autoCompleteData?.status,
    autocompleteSuccessFunc,
    autocompleteErrorFunc,
  );

  // --- Address by ID lookup ---
  const { data: addressData, isSuccess: addressLookupSuccess } = useAddress(
    { params: { id: searchAddressID } },
    searchAddressID ? true : false,
  );

  const changeCountryFormat = (country) => {
    if (
      country === "England" ||
      country === "Wales" ||
      country === "Scotland" ||
      country === "Northern Ireland"
    ) {
      return "GB";
    }
    return "";
  };

  const addressLookupSuccessFunc = () => {
    setValueAddress("address", addressData.data.line_1);
    setValueAddress("address2", addressData.data.line_2);
    setValueAddress("county", addressData.data.county);
    setValueAddress("city", addressData.data.town_or_city);
    setValueAddress("postcode", addressData.data.postcode);
    setValueAddress("country", changeCountryFormat(addressData.data.country));
    setValueAddress("residential", !!addressData.data.residential);
  };
  const addressLookupErrorFunc = () =>
    setAlertNotification({
      message: `Error: ${addressData.status}: ${addressData.statusText}`,
      severity: "error",
    });

  useCheckAPI(
    addressLookupSuccess,
    addressData,
    addressData?.status,
    addressLookupSuccessFunc,
    addressLookupErrorFunc,
  );

  const onInputChange = (e) => {
    const addr = e.target.value;
    if (addr) {
      setSearchAddress(addr);
    } else {
      setAddressOptions([]);
    }
  };

  const handleAddressSearch = (event, value) => {
    setSearchAddressID(value.id);
  };

  // --- Validation ---
  const validationHelper = {
    name: { required: "Name is required" },
    address: { required: "Address is required" },
    address2: { required: "Address 2 is required" },
    county: { required: "County is required" },
    city: { required: "City is required" },
    postcode: { required: "Postcode is required" },
    country: { required: "Country is required" },
    contact: { required: "Contact is required" },
    contactEmail: {
      required: "Contact Email is required",
      pattern: "Invalid email address",
    },
    contactMobile: { required: "Contact Mobile is required" },
  };

  // --- Handlers ---
  const handleClickOpen = () => {
    reset(buildDefaults());
    setConfirmDelete(false);
    setSearchAddress("");
    setSearchAddressID("");
    setAddressOptions([]);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setConfirmDelete(false);
  };

  // Save edited address
  const handleOnSubmit = (formData) => {
    mutateEdit({
      data: {
        filter: { code: addressProp.code },
        address: {
          code: addressProp.code,
          contact: formData.contact,
          name: formData.name,
          address: formData.address,
          address2: formData.address2,
          city: formData.city,
          county: formData.county,
          postCode: formData.postcode,
          country: formData.country,
          homeaddress: !!formData.residential,
          email: formData.contactEmail,
          mobileNo: formData.contactMobile,
          showOnPortal: true,
          branchNo: formData.branchNo,
        },
      },
    });
  };

  // Delete (hide) address — keep all fields unchanged, just set showOnPortal to false
  const handleDelete = () => {
    mutateDelete({
      data: {
        filter: { code: addressProp.code },
        address: {
          code: addressProp.code,
          contact: addressProp.contact || "",
          name: addressProp.name || "",
          address: addressProp.address || "",
          address2: addressProp.address2 || "",
          city: addressProp.city || "",
          county: addressProp.county || "",
          postCode: addressProp.postCode || "",
          country: addressProp.country || "",
          homeaddress: addressProp.homeaddress === "Yes",
          email: addressProp.email || "",
          mobileNo: addressProp.mobileNo || "",
          showOnPortal: false,
          branchNo: addressProp.branchNo || "",
        },
      },
    });
  };

  // --- API result handlers ---
  // Edit success
  const editSuccessFunc = () => {
    setOpen(false);
    setAlertNotification({
      message: "Address updated",
      severity: "info",
    });
  };
  const editErrorFunc = () =>
    setAlertNotification({
      message: `Error: ${editData.data.status}: ${editData.data.statusText}`,
      severity: "warning",
    });

  useCheckAPI(
    editSuccess,
    editData,
    editData?.data?.status,
    editSuccessFunc,
    editErrorFunc,
  );

  // Delete success
  const deleteSuccessFunc = () => {
    setOpen(false);
    setConfirmDelete(false);
    setAlertNotification({
      message: "Address deleted",
      severity: "info",
    });
  };
  const deleteErrorFunc = () =>
    setAlertNotification({
      message: `Error: ${deleteData.data.status}: ${deleteData.data.statusText}`,
      severity: "warning",
    });

  useCheckAPI(
    deleteSuccess,
    deleteData,
    deleteData?.data?.status,
    deleteSuccessFunc,
    deleteErrorFunc,
  );

  // --- Branch data ---
  const { data: dataBranches } = useUserBranches();
  const branchArray = dataBranches?.data.data.data.list.branch;

  // --- Country options ---
  const countryArray = ["", "GB", "IE"];

  return (
    <Box>
      <IconButton
        aria-label="edit-address"
        color="primary"
        onClick={handleClickOpen}
        disabled={!useProtectedContent("addressEdit")}
      >
        <EditIcon />
      </IconButton>
      <Dialog
        fullWidth={true}
        maxWidth={"md"}
        open={open}
        onClose={handleClose}
        aria-labelledby="edit-address-dialog"
        closeAfterTransition={false}
      >
        <DialogTitle id="edit-address-dialog-title">Edit Address</DialogTitle>
        <DialogContent>
          {confirmDelete ? (
            <Box sx={{ py: 2 }}>
              <DialogContentText>
                Are you sure you want to delete address{" "}
                <strong>{addressProp.code}</strong>?
              </DialogContentText>
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  justifyContent: "flex-end",
                  mt: 2,
                }}
              >
                <Button
                  variant="outlined"
                  onClick={() => setConfirmDelete(false)}
                  disabled={deletePending}
                >
                  Go Back
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleDelete}
                  disabled={deletePending}
                >
                  {deletePending ? "Deleting..." : "Confirm Delete"}
                </Button>
              </Box>
            </Box>
          ) : (
            <Grid container spacing={2} sx={{ pt: 1 }}>
              {/* Address Lookup */}
              <Grid size={12}>
                <Autocomplete
                  onInputChange={onInputChange}
                  onChange={handleAddressSearch}
                  filterOptions={(x) => x}
                  freeSolo
                  id="edit-search-address"
                  disableClearable
                  size="small"
                  options={addressOptions}
                  getOptionLabel={(option) => option.address || ""}
                  getOptionKey={(option) => option.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search address.."
                      slotProps={{
                        ...params.slotProps,

                        input: {
                          ...params.slotProps.input,
                          type: "search",
                        },
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid size={12}>
                <Divider />
              </Grid>

              {/* Name */}
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                }}
              >
                <Controller
                  name="name"
                  control={controlAddress}
                  rules={{ required: true }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      fullWidth
                      size="small"
                      label="Name"
                      error={error !== undefined}
                      helperText={
                        error ? validationHelper.name[error.type] : ""
                      }
                    />
                  )}
                />
              </Grid>

              {/* Address */}
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                }}
              >
                <Controller
                  name="address"
                  control={controlAddress}
                  rules={{ required: true }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      fullWidth
                      size="small"
                      label="Address"
                      error={error !== undefined}
                      helperText={
                        error ? validationHelper.address[error.type] : ""
                      }
                    />
                  )}
                />
              </Grid>

              {/* Address 2 */}
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                }}
              >
                <Controller
                  name="address2"
                  control={controlAddress}
                  rules={{ required: false }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      fullWidth
                      size="small"
                      label="Address 2"
                      error={error !== undefined}
                      helperText={
                        error ? validationHelper.address2[error.type] : ""
                      }
                    />
                  )}
                />
              </Grid>

              {/* County */}
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                }}
              >
                <Controller
                  name="county"
                  control={controlAddress}
                  rules={{ required: false }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      fullWidth
                      size="small"
                      label="County"
                      error={error !== undefined}
                      helperText={
                        error ? validationHelper.county[error.type] : ""
                      }
                    />
                  )}
                />
              </Grid>

              {/* City */}
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                }}
              >
                <Controller
                  name="city"
                  control={controlAddress}
                  rules={{ required: true }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      fullWidth
                      size="small"
                      label="City"
                      error={error !== undefined}
                      helperText={
                        error ? validationHelper.city[error.type] : ""
                      }
                    />
                  )}
                />
              </Grid>

              {/* Postcode */}
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                }}
              >
                <Controller
                  name="postcode"
                  control={controlAddress}
                  rules={{ required: true }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      fullWidth
                      size="small"
                      label="Postcode"
                      error={error !== undefined}
                      helperText={
                        error ? validationHelper.postcode[error.type] : ""
                      }
                    />
                  )}
                />
              </Grid>

              {/* Country */}
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                }}
              >
                <Controller
                  name="country"
                  control={controlAddress}
                  rules={{ required: true }}
                  render={({ field, fieldState: { error } }) => (
                    <FormControl fullWidth size="small" error={error ? true : false}>
                      <InputLabel id="edit-country-label">Country</InputLabel>
                      <Select
                        labelId="edit-country-label"
                        {...field}
                        label="Country"
                        error={error !== undefined}
                      >
                        {countryArray.map((country) => (
                          <MenuItem key={country} value={country}>
                            {country}
                          </MenuItem>
                        ))}
                      </Select>
                      {error ? (
                        <FormHelperText>
                          {validationHelper.country[error.type]}
                        </FormHelperText>
                      ) : null}
                    </FormControl>
                  )}
                />
              </Grid>

              {/* Contact */}
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                }}
              >
                <Controller
                  name="contact"
                  control={controlAddress}
                  rules={{ required: true }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      fullWidth
                      size="small"
                      label="Contact"
                      error={error !== undefined}
                      helperText={
                        error ? validationHelper.contact[error.type] : ""
                      }
                    />
                  )}
                />
              </Grid>

              {/* Branch */}
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                }}
              >
                {dataBranches ? (
                  <RHFSelectField
                    name="branchNo"
                    label="Branch"
                    options={branchArray}
                    optionIdentifiers={{ id: "no", label: "name" }}
                    control={controlAddress}
                    validationHelper={validationHelper}
                    rules={{ required: false }}
                    viewMode={false}
                    size="small"
                  />
                ) : (
                  <Skeleton variant="rounded" width="100%" height={40} />
                )}
              </Grid>

              {/* Residential switch */}
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                }}
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Controller
                  name="residential"
                  control={controlAddress}
                  render={({ field: { onChange, value } }) => (
                    <FormControlLabel
                      control={<Switch checked={value} onChange={onChange} />}
                      label="Residential Address"
                    />
                  )}
                />
              </Grid>

              {/* Conditional contact fields */}
              {watchResidential && (
                <>
                  <Grid
                    size={{
                      xs: 12,
                      md: 6,
                    }}
                  >
                    <Controller
                      name="contactEmail"
                      control={controlAddress}
                      rules={{
                        required: true,
                        pattern:
                          /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
                      }}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          {...field}
                          fullWidth
                          size="small"
                          label="Contact Email"
                          error={error !== undefined}
                          helperText={
                            error
                              ? validationHelper.contactEmail[error.type]
                              : ""
                          }
                        />
                      )}
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      md: 6,
                    }}
                  >
                    <Controller
                      name="contactMobile"
                      control={controlAddress}
                      rules={{ required: true }}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          {...field}
                          fullWidth
                          size="small"
                          label="Contact Mobile"
                          error={error !== undefined}
                          helperText={
                            error
                              ? validationHelper.contactMobile[error.type]
                              : ""
                          }
                        />
                      )}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          )}
        </DialogContent>
        {!confirmDelete && (
          <DialogActions>
            <Button
              color="error"
              variant="contained"
              onClick={() => setConfirmDelete(true)}
              disabled={editPending}
            >
              Delete Address
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button onClick={handleClose} disabled={editPending}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit(handleOnSubmit)}
              disabled={editPending}
            >
              {editPending ? "Saving..." : "Save"}
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </Box>
  );
};
